import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import { CorrectedTotalDTO } from '@callmart/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAlertMessage } from '../../../hooks';
import UserContext from '../../../store/User/UserContext';
import { createWorkingHoursRowFilter } from '../../../utils/workingHoursUtils';
import { createTableRows, getFooterRowStyle, parseCellValue } from '../../../utils/agGridUtils';
import { createColumnDefs, createMapWithTableRows, defaultColumnDefs } from '../helpers/utils';
import TableGridItem from '../../UI/TableGridItem';
import { changeCorrectedTotals, checkOspDrafts, tefForceCreate } from '../../../services/correctedTotals';
import { getAllHourSlotsInATimeRange, getSlotDateTime } from '../../../utils/dateUtils';
import { useCellPopover } from '../../../hooks/useCellPopover';
import CardPopover from '../../UI/CardPopover';
import CorrectedTotalsHistory, { VersionEntry } from '../CorrectedTotalsHistory';
import { CorrectedTotalsCellRenderer } from '../CorrectedTotalsCellRenderer';
import type { Dayjs } from '@utils/dayjs';

// Interface for cell data with versions
interface CellDataWithVersions {
    versions?: VersionEntry[] | null;
    comparisonVersions?: VersionEntry[] | null;
    total?: number | null;
    comparisonValue?: number | null;
    isDraftOnly?: boolean;
    draftValue?: number | null;
    [key: string]: any;
}

// Type for new records (without id)
interface NewCorrectedTotalRow {
    value: number;
    workingUnitLocation: number;
    date: string;
}

// Constant hour slots - computed once since parameters never change
const HOUR_SLOTS = getAllHourSlotsInATimeRange({
    startTime: 0,
    endTime: 23,
    displayHoursInHH: true,
    displayMinutesInmm: true,
});

// Helper to create empty grid rows for force edit mode
const createEmptyGridRows = (dates: Dayjs[], workingHours: any[], workingUnitLocation: number): any[] => {
    const rows: any[] = [];

    // Build working hours index for O(1) lookup (same pattern as addRowsInMap)
    const workingHoursIndex = new Map<string, any>();
    workingHours?.forEach((wh: any) => {
        const key = `${wh.dayTimeSlot.day}-${wh.dayTimeSlot.timeSlot}`;
        workingHoursIndex.set(key, wh);
    });

    for (const hourSlot of HOUR_SLOTS) {
        const row: any = {
            id: ['ct', workingUnitLocation, hourSlot].join('-'),
            slotTime: hourSlot,
        };

        dates.forEach((date) => {
            const dayKey = `day${date.format('DD')}`;
            // Determine isWorking from workingHours (day 0=Sun maps to 7, 1-6=Mon-Sat)
            const dayNumberForDate = date.day() === 0 ? 7 : date.day();
            const workingHourKey = `${dayNumberForDate}-${hourSlot}`;
            const workingHour = workingHoursIndex.get(workingHourKey);

            row[dayKey] = {
                date: `${date.format('DD.MM.YYYY')}-${hourSlot}`,
                total: null, // Empty cell - no id, no offeredReportId
                // Keep undefined when no working hours data (don't default to false)
                isWorking: workingHour?.isWorking,
            };
        });

        rows.push(row);
    }
    return rows;
};

interface TotalsCorrectionTabPanelProps {
    allDatesInAMonth: Dayjs[];
    correctedTotals: any[];
    comparisonTotals?: any[];
    biddingCalculated?: any[];
    isLoading?: boolean;
    workingHours?: any[];
    workingUnitLocation: number;
    refetchCorrectedTotals: () => void;
    selectedDate: Dayjs;
    showReleasedTotals: boolean;
    onShowReleasedTotalsChange: (value: boolean) => void;
    hideNonWorkingTime?: boolean;
}

const TotalsCorrectionTabPanel: FC<TotalsCorrectionTabPanelProps> = ({
    allDatesInAMonth,
    correctedTotals,
    comparisonTotals,
    biddingCalculated,
    refetchCorrectedTotals,
    isLoading,
    workingHours,
    workingUnitLocation,
    selectedDate,
    showReleasedTotals,
    hideNonWorkingTime = true,
}) => {
    const { t } = useTranslation();
    const user = useContext(UserContext);
    const gridRef = useRef(null);
    const { dispatchAlert } = useAlertMessage();
    const [changedRows, setChangedRows] = useState<CorrectedTotalDTO[]>([]);
    const [newRows, setNewRows] = useState<NewCorrectedTotalRow[]>([]);
    const [forceEditMode, setForceEditMode] = useState(false);
    const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
    const [showCloseEditModeDialog, setShowCloseEditModeDialog] = useState(false);
    const [isCheckingDrafts, setIsCheckingDrafts] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Extract for static dependency check
    const selectedMonth = selectedDate.format('YYYY-MM');

    // Reset force edit mode and unsaved changes when month or location changes
    useEffect(() => {
        setForceEditMode(false);
        setChangedRows([]);
        setNewRows([]);
    }, [selectedMonth, workingUnitLocation]);

    // Popover for showing cell history on focus
    const { hoveredCell, hoveredCellHistoryList, popoverOpen, popoverClose } = useCellPopover<CellDataWithVersions>();

    const isTefUser = useMemo(() => !!user?.isTEFUser, [user]);
    const hasNoOspData = useMemo(
        () => correctedTotals.length === 0 && (!comparisonTotals || comparisonTotals.length === 0),
        [correctedTotals, comparisonTotals]
    );

    const mapWithTableRowsCorrectedTotals = useMemo(() => {
        return createMapWithTableRows({
            dates: allDatesInAMonth,
            correctedTotals,
            comparisonTotals,
            workingHours,
        });
    }, [allDatesInAMonth, correctedTotals, comparisonTotals, workingHours]);

    // Check if there are any draft-only cells (OSP drafts not yet released)
    // This is computed from the actual table data, not just comparisonTotals existence
    const hasDrafts = useMemo(() => {
        if (!comparisonTotals || comparisonTotals.length === 0) return false;
        // Check if any cell in the data has isDraftOnly flag
        return Array.from(mapWithTableRowsCorrectedTotals.values()).some((row) =>
            Object.values(row).some((cell: any) => cell?.isDraftOnly === true)
        );
    }, [comparisonTotals, mapWithTableRowsCorrectedTotals]);

    const hasUnsavedChanges = changedRows.length > 0 || newRows.length > 0;
    const totalChangesCount = changedRows.length + newRows.length;

    // Create a Set of dates with unsaved changes for highlighting
    const unsavedDates = useMemo(() => {
        const dates = new Set<string>();
        changedRows.forEach((row) => dates.add(row.date));
        newRows.forEach((row) => dates.add(row.date));
        return dates;
    }, [changedRows, newRows]);

    // Check if there are still drafts to release (not yet in pending changes)
    const hasDraftsToRelease = useMemo(() => {
        if (!hasDrafts) return false;
        return Array.from(mapWithTableRowsCorrectedTotals.values()).some((row) =>
            Object.values(row).some(
                (cell: any) =>
                    cell?.isDraftOnly === true &&
                    cell?.date &&
                    !unsavedDates.has(cell.date) &&
                    // Exclude non-working empty cells
                    (cell?.isWorking || cell?.total)
            )
        );
    }, [hasDrafts, mapWithTableRowsCorrectedTotals, unsavedDates]);

    // Check if there are empty working cells that could be filled from bidding totals
    const hasEmptyCellsToFill = useMemo(() => {
        if (!biddingCalculated?.length) return false;

        // Create map of available bidding values by slot
        const biddingMap = new Map<string, number>();
        biddingCalculated.forEach((record: any) => {
            const slot = getSlotDateTime(record.offeredReport?.slotId);
            if (slot && record.total != null && record.total > 0) {
                biddingMap.set(slot, record.total);
            }
        });

        // Check if any cell is empty but has a corresponding bidding value
        // Note: biddingMap only contains values > 0, so we don't need to check value again
        return Array.from(mapWithTableRowsCorrectedTotals.values()).some((row) =>
            Object.values(row).some((cell: any) => {
                if (!cell?.date) return false;
                if (unsavedDates.has(cell.date)) return false;
                return cell.total == null && biddingMap.has(cell.date);
            })
        );
    }, [biddingCalculated, mapWithTableRowsCorrectedTotals, unsavedDates]);

    // Check for unreleased OSP drafts when TEF user tries to force edit
    const { data: ospDraftStatus, refetch: refetchOspDrafts } = useQuery({
        queryKey: ['ospDrafts', workingUnitLocation, selectedDate.format('YYYY-MM')],
        queryFn: () =>
            checkOspDrafts({
                workingUnitLocation,
                // Use YYYY-MM-DD format as expected by the API
                dateFrom: selectedDate.startOf('month').format('YYYY-MM-DD'),
                dateTo: selectedDate.endOf('month').format('YYYY-MM-DD'),
            }),
        enabled: false, // Only fetch when explicitly triggered
    });

    const { mutateAsync: updateCorrectedTotals } = useMutation({ mutationFn: changeCorrectedTotals });

    // Handle saving all changes
    const handleSaveChanges = async () => {
        if (!hasUnsavedChanges) return;

        setIsSaving(true);
        try {
            // Save existing record updates (has id)
            if (changedRows.length > 0) {
                await updateCorrectedTotals(changedRows);
            }

            // Save new records (no id) - use tefForceCreate endpoint
            if (newRows.length > 0) {
                await tefForceCreate({
                    workingUnitLocationId: workingUnitLocation,
                    totals: newRows.map((r) => ({ date: r.date, total: r.value })),
                    released: showReleasedTotals,
                    overwriteOspDrafts: false,
                });
            }

            // Clear pending changes
            setChangedRows([]);
            setNewRows([]);

            dispatchAlert({
                message: t('notification.changesSaved'),
                type: 'success',
            });

            refetchCorrectedTotals();
        } catch (error: any) {
            const zodErrors = error?.response?.data?.errors;
            const errorMessage = zodErrors
                ? Object.entries(zodErrors)
                      .map(([field, msg]) => `${field}: ${msg}`)
                      .join(', ')
                : error?.response?.data?.message || error.message || 'Changes could not be saved';
            dispatchAlert({
                message: errorMessage,
                type: 'error',
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle discarding all changes
    const handleDiscardChanges = () => {
        setChangedRows([]);
        setNewRows([]);
        refetchCorrectedTotals(); // Refresh grid to restore original values
        dispatchAlert({
            message: t('notification.changesDiscarded'),
            type: 'info',
        });
    };

    // Handle closing force edit mode - show confirmation if there are unsaved changes
    const handleCloseForceEditMode = () => {
        if (hasUnsavedChanges) {
            setShowCloseEditModeDialog(true);
        } else {
            setForceEditMode(false);
        }
    };

    // Handle confirmation to close edit mode and discard changes
    const handleCloseEditModeConfirm = () => {
        setShowCloseEditModeDialog(false);
        handleDiscardChanges();
        setForceEditMode(false);
    };

    // Memoize base grid separately - only depends on dates, workingHours, and location
    const baseGrid = useMemo(
        () => createEmptyGridRows(allDatesInAMonth, workingHours || [], workingUnitLocation),
        [allDatesInAMonth, workingHours, workingUnitLocation]
    );

    const tableRowsCorrectedTotals = useMemo(() => {
        // If we have corrected totals data (either released or drafts), merge it into the base grid
        if (
            (correctedTotals.length > 0 || (comparisonTotals && comparisonTotals.length > 0)) &&
            mapWithTableRowsCorrectedTotals.size > 0
        ) {
            const dataRows = createTableRows(mapWithTableRowsCorrectedTotals, 'slotTime');

            // Create a lookup map for quick access to data rows by slotTime
            const dataMap = new Map<string, any>();
            dataRows.forEach((row) => {
                dataMap.set(row.slotTime, row);
            });

            // Merge data into base grid while preserving isWorking from base
            return baseGrid.map((baseRow) => {
                const dataRow = dataMap.get(baseRow.slotTime);
                if (!dataRow) return baseRow;

                // Merge each day column, preserving isWorking from base
                const mergedRow = { ...baseRow };
                allDatesInAMonth.forEach((date) => {
                    const dayKey = `day${date.format('DD')}`;
                    if (dataRow[dayKey]) {
                        mergedRow[dayKey] = {
                            ...dataRow[dayKey],
                            isWorking: baseRow[dayKey]?.isWorking ?? dataRow[dayKey]?.isWorking,
                        };
                    }
                });
                return mergedRow;
            });
        }

        return baseGrid;
    }, [baseGrid, mapWithTableRowsCorrectedTotals, correctedTotals.length, comparisonTotals, allDatesInAMonth]);

    // Filter rows to hide non-working time slots if toggle is enabled
    // Also merge pending edits so they survive React re-renders
    const filteredTableRows = useMemo(() => {
        let rows = tableRowsCorrectedTotals;

        // Merge pending edits into rows so they survive React re-renders
        if (changedRows.length > 0 || newRows.length > 0) {
            // Build lookup maps for pending changes
            const changedMap = new Map(changedRows.map((r) => [r.date, r.value]));
            const newMap = new Map(newRows.map((r) => [r.date, r.value]));

            rows = rows.map((row) => {
                const updatedRow = { ...row };
                let hasChanges = false;

                allDatesInAMonth.forEach((date) => {
                    const dayKey = `day${date.format('DD')}`;
                    const slot = row[dayKey]?.date;
                    if (!slot) return;

                    const changedValue = changedMap.get(slot);
                    const newValue = newMap.get(slot);
                    const pendingValue = changedValue ?? newValue;

                    if (pendingValue !== undefined) {
                        updatedRow[dayKey] = {
                            ...updatedRow[dayKey],
                            total: pendingValue,
                            // Clear draft status for cells with pending changes (approved drafts)
                            isDraftOnly: false,
                            draftValue: undefined,
                        };
                        hasChanges = true;
                    }
                });

                return hasChanges ? updatedRow : row;
            });
        }

        // Apply working hours filter
        const filterFn = createWorkingHoursRowFilter(allDatesInAMonth, workingHours, hideNonWorkingTime);
        return rows.filter(filterFn);
    }, [tableRowsCorrectedTotals, hideNonWorkingTime, allDatesInAMonth, changedRows, newRows, workingHours]);

    // Calculate max value for gradient coloring, excluding draft-only cells
    const maxTotalCorrectionValue = useMemo(() => {
        let max = 0;
        for (const [, rowData] of mapWithTableRowsCorrectedTotals.entries()) {
            for (const cell of Object.values(rowData) as any[]) {
                // Only include released values (not draft-only) in max calculation
                if (cell?.total != null && !cell?.isDraftOnly && cell.total > max) {
                    max = cell.total;
                }
            }
        }
        return max;
    }, [mapWithTableRowsCorrectedTotals]);

    const canEditTotals = useMemo(() => {
        if (!isTefUser) return false;
        // Require forceEditMode when there's no OSP data or when there are drafts
        if (hasNoOspData || hasDrafts) return forceEditMode;
        return true;
    }, [isTefUser, hasNoOspData, hasDrafts, forceEditMode]);

    // Enable force edit mode and auto-fill from totals if applicable
    const enableForceEditWithAutoFill = useCallback(() => {
        setForceEditMode(true);
        // Auto-fill from totals when there's no OSP data
        if (hasNoOspData && biddingCalculated?.length) {
            // Small delay to ensure grid is fully rendered after state change
            setTimeout(() => {
                handleCopyFromTotals();
            }, 100);
        }
    }, [hasNoOspData, biddingCalculated?.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle enabling force edit mode
    const handleEnableForceEdit = async () => {
        // If we already know there are drafts (hasDrafts), skip the check and enable directly
        if (hasDrafts) {
            setForceEditMode(true);
            return;
        }
        // For hasNoOspData scenario, check if there are unreleased OSP drafts
        setIsCheckingDrafts(true);
        try {
            const result = await refetchOspDrafts();
            if (result.data?.hasUnreleasedDrafts) {
                setShowOverwriteDialog(true);
            } else {
                enableForceEditWithAutoFill();
            }
        } finally {
            setIsCheckingDrafts(false);
        }
    };

    // Handle overwrite confirmation
    const handleOverwriteConfirm = (overwrite: boolean) => {
        setShowOverwriteDialog(false);
        if (overwrite) {
            enableForceEditWithAutoFill();
        }
    };

    // Handle filling empty cells with values from bidding totals
    const handleCopyFromTotals = () => {
        if (!biddingCalculated?.length || !gridRef.current) return;

        // Create map of bidding values by slot
        const biddingMap = new Map<string, number>();
        biddingCalculated.forEach((record: any) => {
            const slot = getSlotDateTime(record.offeredReport?.slotId);
            if (slot && record.total != null) {
                biddingMap.set(slot, record.total);
            }
        });

        // Build sets of already pending slots to avoid overwriting user edits
        const pendingChangedSlots = new Set(changedRows.map((r) => r.date));
        const pendingNewSlots = new Set(newRows.map((r) => r.date));

        // Get current grid data and update empty cells
        const api = (gridRef.current as any).api;
        const newRowsToCreate: NewCorrectedTotalRow[] = [];
        const cellsToRefresh: { rowNode: any; columns: string[] }[] = [];

        api.forEachNode((node: any) => {
            if (node.data && !node.footer) {
                const columnsToRefresh: string[] = [];
                allDatesInAMonth.forEach((date) => {
                    const dayKey = `day${date.format('DD')}`;
                    const cellData = node.data[dayKey];
                    const slot = `${date.format('DD.MM.YYYY')}-${node.data.slotTime}`;
                    const biddingValue = biddingMap.get(slot);

                    // Skip if: cell already has value, no bidding value, or (non-working cell with zero value)
                    if (cellData?.total != null || biddingValue == null || (!cellData?.isWorking && biddingValue <= 0))
                        return;
                    if (pendingChangedSlots.has(slot) || pendingNewSlots.has(slot)) return;

                    // Update cell value in grid
                    if (!node.data[dayKey]) {
                        node.data[dayKey] = { date: slot };
                    }
                    node.data[dayKey].total = biddingValue;
                    columnsToRefresh.push(dayKey);

                    // CREATE new record (empty cells don't have id)
                    newRowsToCreate.push({
                        value: biddingValue,
                        workingUnitLocation,
                        date: slot,
                    });
                });
                if (columnsToRefresh.length > 0) {
                    cellsToRefresh.push({ rowNode: node, columns: columnsToRefresh });
                }
            }
        });

        if (newRowsToCreate.length > 0) {
            // Redraw only the affected rows to show updated values (preserves node.data)
            const rowNodes = cellsToRefresh.map(({ rowNode }) => rowNode);
            api.redrawRows({ rowNodes });

            setNewRows((prev) => prev.concat(newRowsToCreate));
            dispatchAlert({
                message: t('notification.cellsFilledCount', { count: newRowsToCreate.length }),
                type: 'success',
            });
        } else {
            dispatchAlert({
                message: t('bidding.noTotalsToFill'),
                type: 'info',
            });
        }
    };

    // Release all draft cells at once (approve all OSP drafts)
    const handleReleaseAllDrafts = () => {
        if (!gridRef.current) return;

        const api = (gridRef.current as any).api;
        const draftsToUpdate: CorrectedTotalDTO[] = [];
        const draftsToCreate: NewCorrectedTotalRow[] = [];

        api.forEachNode((node: any) => {
            if (node.data && !node.footer) {
                allDatesInAMonth.forEach((date) => {
                    const dayKey = `day${date.format('DD')}`;
                    const cellData = node.data[dayKey];

                    // Skip if not a draft-only cell
                    if (!cellData?.isDraftOnly) return;
                    // Skip non-working cells with zero/undefined value
                    if (!cellData?.isWorking && !cellData?.total) return;
                    // Skip if already in pending changes
                    if (unsavedDates.has(cellData.date)) return;

                    if (cellData.id) {
                        // Has existing released record - update it
                        draftsToUpdate.push({
                            id: cellData.id,
                            offeredReportId: cellData.offeredReportId,
                            value: cellData.total,
                            workingUnitLocation,
                            date: cellData.date,
                        });
                    } else {
                        // Draft-only without released record - create new
                        draftsToCreate.push({
                            value: cellData.total,
                            workingUnitLocation,
                            date: cellData.date,
                        });
                    }
                });
            }
        });

        const totalCount = draftsToUpdate.length + draftsToCreate.length;
        if (totalCount > 0) {
            if (draftsToUpdate.length > 0) {
                setChangedRows((prev) => [...prev, ...draftsToUpdate]);
            }
            if (draftsToCreate.length > 0) {
                setNewRows((prev) => [...prev, ...draftsToCreate]);
            }
            dispatchAlert({
                message: t('notification.draftsReleasedCount', { count: totalCount }),
                type: 'success',
            });
        } else {
            dispatchAlert({
                message: t('bidding.noDraftsToRelease'),
                type: 'info',
            });
        }
    };

    const handleCellValueChanged = (params: CellValueChangedEvent) => {
        const colId = params.column.getColId();
        const value = parseCellValue(params.value);
        const oldValue = parseCellValue(params.oldValue);

        // Initialize cell data if it doesn't exist (force edit mode with empty cells)
        if (!params.data[colId]) {
            // Extract date from column id (format: dayXX)
            const dayNum = colId.replace('day', '');
            const dateForCell = allDatesInAMonth.find((d) => d.format('DD') === dayNum);
            params.data[colId] = {
                date: dateForCell ? `${dateForCell.format('DD.MM.YYYY')}-${params.data.slotTime}` : null,
            };
        }

        const cellData = params.data[colId];

        if (value === undefined) {
            cellData.hasError = true;
            dispatchAlert({
                message: t('validation.validNumber'),
                type: 'error',
            });
            return;
        }

        // Clear error state for valid inputs
        cellData.hasError = false;

        if (value !== oldValue && cellData.date) {
            if (cellData.id) {
                // UPDATE existing record - check if already in changedRows
                setChangedRows((prev) => {
                    const existingIndex = prev.findIndex((r) => r.id === cellData.id);
                    if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...updated[existingIndex], value };
                        return updated;
                    }
                    return prev.concat({
                        id: cellData.id,
                        offeredReportId: cellData.offeredReportId,
                        value,
                        workingUnitLocation,
                        date: cellData.date,
                    });
                });
            } else if (value !== null) {
                // CREATE new record - check if already in newRows by date
                setNewRows((prev) => {
                    const existingIndex = prev.findIndex((r) => r.date === cellData.date);
                    if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...updated[existingIndex], value };
                        return updated;
                    }
                    return prev.concat({
                        value,
                        workingUnitLocation,
                        date: cellData.date,
                    });
                });
            }
        }
    };

    // Handle history icon click to show history popover
    const handleHistoryClick = useCallback(
        (cellData: CellDataWithVersions, event: React.MouseEvent) => {
            // Show popover if cell has versions, comparison diff, or is draft-only
            const hasVersions = cellData?.versions && cellData.versions.length > 0;
            const hasComparisonDiff = cellData?.comparisonValue != null;
            const isDraftOnly = cellData?.isDraftOnly === true;

            if (!hasVersions && !hasComparisonDiff && !isDraftOnly) {
                return;
            }

            // Use the click target as anchor element
            const anchorEl = event.currentTarget as HTMLElement;
            popoverOpen(true, anchorEl, cellData);
        },
        [popoverOpen]
    );

    // Handle approve button click - approve draft value as released
    const handleApproveClick = useCallback(
        (cellData: CellDataWithVersions) => {
            if (!cellData?.date) return;

            // Get the draft value to approve
            // For isDraftOnly: the displayed value IS the draft (cellData.total)
            // For hasComparisonDiff: the draft is in comparisonValue
            const isDraftOnly = cellData?.isDraftOnly === true;
            const draftValue = isDraftOnly ? cellData.total : cellData.comparisonValue;

            if (draftValue == null) return;

            if (cellData.id) {
                // UPDATE existing released record with the draft value
                setChangedRows((prev) => {
                    const existingIndex = prev.findIndex((r) => r.id === cellData.id);
                    if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...updated[existingIndex], value: draftValue };
                        return updated;
                    }
                    return prev.concat({
                        id: cellData.id,
                        offeredReportId: cellData.offeredReportId,
                        value: draftValue,
                        workingUnitLocation,
                        date: cellData.date,
                    });
                });
            } else {
                // CREATE new released record from draft-only value
                setNewRows((prev) => {
                    const existingIndex = prev.findIndex((r) => r.date === cellData.date);
                    if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...updated[existingIndex], value: draftValue };
                        return updated;
                    }
                    return prev.concat({
                        value: draftValue,
                        workingUnitLocation,
                        date: cellData.date,
                    });
                });
            }

            // Draft status is cleared immutably via filteredTableRows memo
            // when the cell's date is added to changedRows/newRows

            dispatchAlert({
                message: t('notification.draftApproved'),
                type: 'success',
            });
        },
        [workingUnitLocation, dispatchAlert, t]
    );

    // Memoize column definitions to avoid re-creation on every render
    const columnDefs = useMemo(
        () =>
            createColumnDefs({
                allDatesInAMonth,
                valuePropertyName: 'total',
                hasFooter: true,
                footerRowText: t('common.sum'),
                editable: canEditTotals,
                alwaysAllowEdit: canEditTotals,
                cellRenderer: CorrectedTotalsCellRenderer,
                cellRendererParams: {
                    onHistoryClick: handleHistoryClick,
                    onApproveClick: handleApproveClick,
                    maxValue: maxTotalCorrectionValue,
                    canEdit: canEditTotals,
                    unsavedDates,
                },
                // Override AG Grid's default edited cell background - we handle styling in the cell renderer
                cellStyleRenderer: ({ node }) => ({
                    backgroundColor: node?.isRowPinned() ? undefined : 'white',
                }),
            }),
        [
            allDatesInAMonth,
            canEditTotals,
            handleHistoryClick,
            handleApproveClick,
            maxTotalCorrectionValue,
            t,
            unsavedDates,
        ]
    );

    return (
        <>
            {/* Stable wrapper for alerts and toolbar - prevents React DOM reconciliation errors */}
            <Box>
                {/* Alert for TEF users when no data or drafts exist */}
                {isTefUser && (hasNoOspData || hasDrafts) && !forceEditMode && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography>
                            <span>{hasNoOspData ? t('bidding.noOspData') : t('bidding.ospDraftsExist')}</span>
                        </Typography>
                        <LoadingButton
                            variant="contained"
                            size="small"
                            onClick={handleEnableForceEdit}
                            loading={isCheckingDrafts}
                            sx={{ mt: 1 }}
                        >
                            <span>{t('action.enableEditing')}</span>
                        </LoadingButton>
                    </Alert>
                )}

                {/* Force Edit Mode Active Indicator */}
                {isTefUser && forceEditMode && (
                    <Alert severity="warning" sx={{ mb: 2 }} onClose={handleCloseForceEditMode}>
                        <Typography variant="body2">{t('hint.forceEditMode')}</Typography>
                    </Alert>
                )}

                {/* Toolbar for TEF users */}
                {isTefUser && !((hasNoOspData || hasDrafts) && !forceEditMode) && (
                    <Box sx={{ pt: 1, pb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Release all drafts button - only when unreleased drafts exist */}
                        {hasDraftsToRelease && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleReleaseAllDrafts}
                                disabled={!canEditTotals}
                            >
                                {t('bidding.releaseAllDrafts')}
                            </Button>
                        )}

                        {/* Fill cells from totals button - only when there are empty cells to fill */}
                        {hasEmptyCellsToFill && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCopyFromTotals}
                                disabled={!canEditTotals}
                            >
                                {t('bidding.fillCellsFromTotals')}
                            </Button>
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Discard Changes button - only visible when there are changes */}
                        {hasUnsavedChanges && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="inherit"
                                onClick={handleDiscardChanges}
                                disabled={isSaving}
                            >
                                {t('action.discardChanges')}
                            </Button>
                        )}

                        {/* Save Changes button - always visible, disabled when no changes */}
                        <LoadingButton
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleSaveChanges}
                            loading={isSaving}
                            disabled={!hasUnsavedChanges}
                        >
                            <span>
                                {hasUnsavedChanges
                                    ? t('action.saveChangesCount', { count: totalChangesCount })
                                    : t('action.saveChanges')}
                            </span>
                        </LoadingButton>
                    </Box>
                )}
            </Box>

            {/* Main Grid */}
            <TableGridItem
                ref={gridRef}
                rowData={filteredTableRows}
                defaultColDef={defaultColumnDefs}
                columnDefs={columnDefs}
                columnHoverHighlight={true}
                grandTotalRow={'bottom'}
                getRowStyle={getFooterRowStyle}
                cellSelection={true}
                loading={isLoading}
                onCellValueChanged={handleCellValueChanged}
                suppressScrollOnNewData={true}
            />

            {/* Overwrite Confirmation Dialog */}
            <Dialog open={showOverwriteDialog} onClose={() => setShowOverwriteDialog(false)}>
                <DialogTitle>{t('dialog.unreleasedDraftsTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('dialog.unreleasedDraftsPrompt', { count: ospDraftStatus?.draftCount || 0 })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleOverwriteConfirm(false)}>{t('bidding.waitForOsp')}</Button>
                    <Button onClick={() => handleOverwriteConfirm(true)} color="warning" variant="contained">
                        {t('bidding.overwriteDrafts')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Close Edit Mode Confirmation Dialog */}
            <Dialog open={showCloseEditModeDialog} onClose={() => setShowCloseEditModeDialog(false)}>
                <DialogTitle>{t('dialog.discardUnsavedChanges')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('dialog.unsavedChangesWarning')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCloseEditModeDialog(false)}>{t('action.cancel')}</Button>
                    <Button onClick={handleCloseEditModeConfirm} color="warning" variant="contained">
                        {t('action.discardChanges')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Popover - shows on cell focus */}
            <CardPopover anchorEl={hoveredCell} onClose={popoverClose}>
                {hoveredCellHistoryList && (
                    <CorrectedTotalsHistory
                        versions={[
                            ...(hoveredCellHistoryList.versions || []),
                            ...(hoveredCellHistoryList.comparisonVersions || []),
                        ]}
                        releasedValue={hoveredCellHistoryList.isDraftOnly ? null : hoveredCellHistoryList.total}
                        draftValue={
                            hoveredCellHistoryList.isDraftOnly
                                ? hoveredCellHistoryList.total
                                : hoveredCellHistoryList.comparisonValue
                        }
                    />
                )}
            </CardPopover>
        </>
    );
};

export default TotalsCorrectionTabPanel;
